import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { Alert, AlertDialog, Button, IconButton, Popover, Spinner } from '@ntconsult/aluma-react';
import { AlertTriangle, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEscritasAxiis, useRetentarEscritas } from '../hooks';
import type { Campanha } from '../types/Campanha';

export function EscritasAxiisSecao({ campanha }: { campanha: Campanha }) {
  const { t } = useTranslation(['escritas', 'common', 'erros']);
  const { data, isLoading, isError, error } = useEscritasAxiis(campanha.id);
  const { retentar, isPending } = useRetentarEscritas(campanha.id);
  const [confirmando, setConfirmando] = useState(false);
  const navigate = useNavigate();
  const linhas = data
    ? ([
        ['camiseta', data.camiseta],
        ['endereco', data.enderecoOficial],
      ] as const)
    : [];
  const temPendencia =
    !!data && [data.camiseta, data.enderecoOficial].some((c) => c.pendente + c.falha > 0);

  // Ícone no cartão "Responderam" (alerta com pendência ou falha); o detalhe abre num popover.
  // O AlertDialog fica fora do popover: clicar nele fecharia o popover e o desmontaria.
  return (
    <>
      <Popover
        placement="bottom-end"
        trigger={
          <IconButton
            size="sm"
            icon={
              temPendencia ? (
                <AlertTriangle size={16} className="text-warning" />
              ) : (
                <Info size={16} />
              )
            }
            label={t('escritas:titulo')}
          />
        }
      >
        <section className="flex min-w-72 flex-col gap-2 text-sm">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h3 className="text-sm font-semibold text-foreground">{t('escritas:titulo')}</h3>
            {temPendencia && campanha.ativa && (
              <Button
                variant="secondary"
                size="sm"
                loading={isPending}
                onClick={() => setConfirmando(true)}
              >
                {t('escritas:retentar')}
              </Button>
            )}
          </div>
          {isLoading && <Spinner label={t('common:loading')} />}
          {isError && <Alert variant="danger" title={t(`erros:${mensagemDeErro(error)}`)} />}
          <div className="flex flex-col gap-1">
            {linhas.map(([chave, c]) => (
              <div key={chave} className="flex flex-wrap items-center gap-x-2">
                <span className="text-foreground-muted">{t(`escritas:${chave}`)}</span>
                <span className="text-foreground">
                  {t('escritas:status.sucesso', { n: c.sucesso })}
                </span>
                {/* Pendente e falha, só a partir de 1, levam à lista filtrada (spec 29 R7). */}
                {(['pendente', 'falha'] as const)
                  .filter((s) => c[s] >= 1)
                  .map((s) => (
                    <Button
                      key={s}
                      variant="ghost"
                      size="sm"
                      data-escrita={s}
                      leftIcon={
                        s === 'falha' ? (
                          <AlertTriangle aria-hidden className="text-warning" />
                        ) : undefined
                      }
                      // No painel, que já mostra a lista (spec 36 R12).
                      onClick={() => navigate({ search: `?axiis=${s}` })}
                    >
                      {t(`escritas:status.${s}`, { n: c[s] })}
                    </Button>
                  ))}
              </div>
            ))}
          </div>
        </section>
      </Popover>
      <AlertDialog open={confirmando} onOpenChange={setConfirmando}>
        <AlertDialog.Content>
          <AlertDialog.Title>{t('escritas:retentar')}</AlertDialog.Title>
          <AlertDialog.Description>{t('escritas:confirmarRetentar')}</AlertDialog.Description>
          <AlertDialog.Footer>
            <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
            <AlertDialog.Action
              variant="primary"
              loading={isPending}
              onClick={async () => {
                await retentar();
                setConfirmando(false);
              }}
            >
              {t('escritas:retentar')}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </>
  );
}
